import {
  type FieldError,
  firstPerPath,
  type Refinement,
  refusalsFrom,
} from '@porotutu/contracts/common'
import { schemaFailures } from '@porotutu/http/contact'
import type { Translator } from '@porotutu/i18n'
import type { Static, TSchema } from '@sinclair/typebox'
import { Value } from '@sinclair/typebox/value'
import { useEffect } from 'react'
import {
  type DefaultValues,
  type FieldErrors,
  type FieldValues,
  type Path,
  type Resolver,
  type UseFormProps,
  type UseFormReturn,
  useForm,
} from 'react-hook-form'
import { useT } from '@/session/useSession'

const formPath = (path: string) => {
  const trimmed = path.replace(/^\//, '')

  return trimmed === 'base' ? 'root' : trimmed.replaceAll('/', '.')
}

export type ContractOptions<T extends FieldValues, Body> = {
  defaultValues: T
  refine?: Refinement<Body>[]
  transform?: (values: T) => Body
}

export function refusalsOf<Body>(
  schema: TSchema,
  body: Body,
  refine: Refinement<Body>[] = [],
): FieldError[] {
  return firstPerPath([
    ...schemaFailures([...Value.Errors(schema, body)]),
    ...refusalsFrom(body, refine),
  ])
}

const errorsFrom = <T extends FieldValues>(failures: FieldError[], t: Translator) => {
  const errors: FieldErrors<T> = {}

  for (const failure of failures) {
    const path = formPath(failure.path)
    const message = t(failure.message, failure.vars ?? {})
    const parts = path.split('.')
    let node = errors as Record<string, unknown>

    for (const part of parts.slice(0, -1)) {
      node[part] = (node[part] as Record<string, unknown>) ?? {}
      node = node[part] as Record<string, unknown>
    }

    node[parts[parts.length - 1] as string] = { type: 'contract', message }
  }

  return errors
}

export function resolverFor<T extends FieldValues, S extends TSchema>(
  schema: S,
  t: Translator,
  options: Pick<ContractOptions<T, Static<S>>, 'refine' | 'transform'> = {},
): Resolver<T> {
  return async (values) => {
    const body = (options.transform?.(values) ?? values) as Static<S>
    const failures = refusalsOf(schema, body, options.refine ?? [])

    if (failures.length === 0) return { values, errors: {} }

    return { values: {}, errors: errorsFrom<T>(failures, t) }
  }
}

export function useContractForm<T extends FieldValues, S extends TSchema>(
  schema: S,
  options: ContractOptions<T, Static<S>> & Omit<UseFormProps<T>, 'defaultValues'>,
): UseFormReturn<T> {
  const t = useT()
  const { refine, transform, defaultValues, ...formOptions } = options

  return useForm<T>({
    ...formOptions,
    defaultValues: defaultValues as DefaultValues<T>,
    resolver: resolverFor<T, S>(schema, t, { refine, transform }),
  })
}

export function useConfirmation<T extends FieldValues>(
  form: UseFormReturn<T>,
  field: Path<T>,
  against: Path<T>,
) {
  const source = form.watch(against)

  useEffect(() => {
    const typed: unknown = form.getValues(field)
    const { error } = form.getFieldState(field)

    if (error === undefined && (typed === '' || typed === source)) return

    void form.trigger(field)
  }, [form, field, source])
}
